import { gql } from '@apollo/client';

/**
 * Stripe Quote Generator (Billing Console) — native Stripe quotes + PDF.
 * Server: apps/api/src/app/stripe-quote/. Every amount is priced server-side; the inputs carry
 * product keys and quantities only.
 */

export const STRIPE_QUOTE_CATALOG = gql`
  query StripeQuoteCatalog {
    stripeQuoteCatalog {
      plans {
        alias
        name
        unitAmountMinor
        minScreens
      }
      addOns {
        key
        label
        quantified
        tiers
        requiresPlan
        unitAmountMinor
      }
      hardware {
        sku
        stripePriceId
        name
        unitAmountMinor
        quotable
      }
      coupons {
        couponKey
        label
        discountSummary
      }
      couponsUnavailable
      freightConfigured
      stripeReady
    }
  }
`;

const CUSTOMER_FIELDS = `
  id
  name
  email
  currency
  country
  addressSummary
  customerCoupon
  warnings
`;

export const STRIPE_QUOTE_CUSTOMER = gql`
  query StripeQuoteCustomer($stripeCustomerId: String!) {
    stripeQuoteCustomer(stripeCustomerId: $stripeCustomerId) {
      ${CUSTOMER_FIELDS}
    }
  }
`;

/**
 * Hardware + Standard US freight only (no customer, no Stripe write). The PDF Quote
 * Generator's Hardware (US only) section prices from this so both tools quote the same freight.
 */
export const STRIPE_QUOTE_HARDWARE_PRICE = gql`
  query StripeQuoteHardwarePrice($hardware: [StripeQuoteHardwareInput!]!) {
    stripeQuoteHardwarePrice(hardware: $hardware) {
      lines {
        kind
        key
        label
        quantity
        unitAmountMinor
        amountMinor
      }
    }
  }
`;

export const STRIPE_QUOTE_PREVIEW = gql`
  query StripeQuotePreview($input: StripeQuoteInput!) {
    stripeQuotePreview(input: $input) {
      customer {
        ${CUSTOMER_FIELDS}
      }
      lines {
        kind
        label
        quantity
        unitAmountMinor
        amountMinor
        recurring
        couponKey
        couponLabel
        discountMinor
      }
      recurringSubtotalMinor
      recurringDiscountMinor
      oneTimeSubtotalMinor
      totalBeforeTaxMinor
      incomplete
    }
  }
`;

export const STRIPE_QUOTE_CREATE = gql`
  mutation StripeQuoteCreate($input: StripeQuoteInput!) {
    stripeQuoteCreate(input: $input) {
      quoteId
      number
      status
      amountTotalMinor
      amountTaxMinor
      currency
      expiresAt
      dashboardUrl
      recorded
      finalizeError
    }
  }
`;

export const STRIPE_QUOTES = gql`
  query StripeQuotes($stripeCustomerId: String, $limit: Int, $mine: Boolean) {
    stripeQuotes(stripeCustomerId: $stripeCustomerId, limit: $limit, mine: $mine) {
      quoteId
      number
      stripeCustomerId
      customerName
      status
      amountTotalMinor
      currency
      expiresAt
      createdBy
      createdAt
      lastError
      dashboardUrl
    }
  }
`;

export const STRIPE_QUOTE_EXTEND = gql`
  mutation StripeQuoteExtend($quoteId: String!) {
    stripeQuoteExtend(quoteId: $quoteId) {
      quoteId
      number
      status
      amountTotalMinor
      amountTaxMinor
      currency
      expiresAt
      dashboardUrl
      recorded
      finalizeError
    }
  }
`;
